/**
 * One exam paper out of GET /Parent/exam/schedule/class/{classId}, for the exam details card
 * an "Upcoming Exam" alert opens (QA, 31 Oct 2026: the alert went to the Homework page).
 *
 * The schedule carries the exam, subject, date, times, class, class teacher, room and
 * invigilator. It has no instructions or documents for a paper, so the card says none were
 * shared rather than leaving them out silently.
 */

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Which paper to open: the exam, and the subject on its date. */
export interface ExamFocus {
  examId: string | null;
  subjectId: string | null;
  examDate: string;
}

export interface ExamDetail {
  examId: string | null;
  examName: string;
  subject: string;
  /** "Sat 31 Oct 2026". */
  date: string;
  /** "09:00 – 11:00 (2 hrs)", or null when no time is set. */
  time: string | null;
  className: string | null;
  teacher: string | null;
  room: string | null;
  invigilator: string | null;
  maxMarks: number | null;
}

const pick = (o: any, ...keys: string[]) => {
  for (const k of keys) { if (o?.[k] !== undefined && o?.[k] !== null && o?.[k] !== '') { return o[k]; } }
  return undefined;
};
const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
const sameDay = (a: string, b: string) => !!a && !!b && a.slice(0, 10) === b.slice(0, 10);

/** "Sat 31 Oct 2026" from the date as written, so no time zone moves it a day. */
export function examDayLabel(value: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  if (!m) { return value || '-'; }
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "09:00 – 11:00 (2 hrs)"; a midnight-to-midnight paper has no time set. */
export function examTimeLabel(start?: string | null, end?: string | null, duration?: string | null): string | null {
  const s = (start ?? '').slice(0, 5);
  const e = (end ?? '').slice(0, 5);
  if (!s || (s === '00:00' && (!e || e === '00:00'))) { return null; }
  const span = e && e !== s ? `${s} – ${e}` : s;
  return text(duration) ? `${span} (${text(duration)})` : span;
}

/** Every paper in the schedule with the exam and class it belongs to. */
function papers(schedule: any[] | null | undefined) {
  const found: { exam: any; cls: any; paper: any }[] = [];
  (Array.isArray(schedule) ? schedule : []).forEach(exam => {
    (pick(exam, 'classSchedules', 'ClassSchedules') ?? []).forEach((cls: any) => {
      (pick(cls, 'subjectTimetable', 'SubjectTimetable') ?? []).forEach((paper: any) => found.push({ exam, cls, paper }));
    });
  });
  return found;
}

/** The next paper from now up to `days` ahead, as the Home alert shows it - or null. */
export function nextExam(schedule: any[] | null | undefined, now: Date, days = 3):
  { focus: ExamFocus; subject: string; examDate: string; daysUntil: number } | null {
  const end = now.getTime() + days * 24 * 60 * 60 * 1000;
  const next = papers(schedule)
    .map(p => ({ ...p, at: new Date(pick(p.paper, 'examDate', 'ExamDate') ?? '').getTime() }))
    .filter(p => !Number.isNaN(p.at) && p.at >= now.getTime() && p.at <= end)
    .sort((a, b) => a.at - b.at)[0];
  if (!next) { return null; }
  const examDate = String(pick(next.paper, 'examDate', 'ExamDate'));
  return {
    focus: {
      examId: pick(next.exam, 'examId', 'ExamId', 'id') ?? null,
      subjectId: pick(next.paper, 'subjectId', 'SubjectId') ?? null,
      examDate,
    },
    subject: text(pick(next.paper, 'subjectName', 'SubjectName')) ?? 'Exam',
    examDate,
    daysUntil: Math.ceil((next.at - now.getTime()) / (24 * 60 * 60 * 1000)),
  };
}

/** The paper a focus points at, with everything the details card shows; null when it is gone. */
export function examDetail(schedule: any[] | null | undefined, focus: ExamFocus | null | undefined): ExamDetail | null {
  if (!focus) { return null; }
  const all = papers(schedule);
  const match = all.find(p => (!focus.examId || pick(p.exam, 'examId', 'ExamId', 'id') === focus.examId)
      && (!focus.subjectId || pick(p.paper, 'subjectId', 'SubjectId') === focus.subjectId)
      && sameDay(String(pick(p.paper, 'examDate', 'ExamDate') ?? ''), focus.examDate))
    // A schedule reloaded since the alert may have a new exam id - the subject on that day still finds it.
    ?? all.find(p => (!focus.subjectId || pick(p.paper, 'subjectId', 'SubjectId') === focus.subjectId)
      && sameDay(String(pick(p.paper, 'examDate', 'ExamDate') ?? ''), focus.examDate));
  if (!match) { return null; }
  const { exam, cls, paper } = match;
  const marks = Number(pick(paper, 'maxMarks', 'MaxMarks'));
  return {
    examId: pick(exam, 'examId', 'ExamId', 'id') ?? null,
    examName: text(pick(exam, 'name', 'Name', 'examTypeName', 'ExamTypeName')) ?? 'Exam',
    subject: text(pick(paper, 'subjectName', 'SubjectName')) ?? 'Exam',
    date: examDayLabel(String(pick(paper, 'examDate', 'ExamDate') ?? '')),
    time: examTimeLabel(pick(paper, 'startTime', 'StartTime'), pick(paper, 'endTime', 'EndTime'), pick(paper, 'duration', 'Duration')),
    className: text(pick(cls, 'className', 'ClassName')),
    teacher: text(pick(cls, 'teacher', 'Teacher')),
    room: text(pick(paper, 'room', 'Room')),
    invigilator: text(pick(paper, 'invigilator', 'Invigilator')),
    maxMarks: Number.isFinite(marks) && marks > 0 ? marks : null,
  };
}

/** The card's rows, in order; anything the school has not set reads "Not set yet". */
export function examDetailRows(d: ExamDetail): { label: string; value: string }[] {
  const or = (v: string | number | null) => (v === null || v === '' ? 'Not set yet' : String(v));
  return [
    { label: 'Exam', value: d.examName },
    { label: 'Subject', value: d.subject },
    { label: 'Date', value: d.date },
    { label: 'Time', value: or(d.time) },
    { label: 'Class', value: or(d.className) },
    { label: 'Class teacher', value: or(d.teacher) },
    { label: 'Room', value: or(d.room) },
    { label: 'Invigilator', value: or(d.invigilator) },
    { label: 'Max marks', value: or(d.maxMarks) },
  ];
}
